import { useEffect, useState } from 'react';
import { hostListProperties } from '../api/host';
import * as hostReviewsApi from '../api/hostReviews';
import { HostSection } from '../components/host/HostLayout';
import { useToast } from '../components/ToastContext';

const statusTone = (status) => {
  const value = (status || '').toLowerCase();
  if (value === 'published') return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (value === 'pending') return 'bg-amber-50 text-amber-700 ring-amber-100';
  if (value === 'hidden' || value === 'removed') return 'bg-rose-50 text-rose-700 ring-rose-100';
  return 'bg-slate-100 text-slate-700 ring-slate-200';
};

const SummaryCard = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-sm text-slate-600">{label}</p>
    <p className="text-2xl font-semibold text-slate-900">{value}</p>
  </div>
);

const HostReviews = ({ accessToken }) => {
  const toast = useToast();
  const [filters, setFilters] = useState({ property_id: '', status: '', rating: '', q: '' });
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ avg: 0, total: 0, responseRate: 0 });
  const [properties, setProperties] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [events, setEvents] = useState([]);
  const [responseText, setResponseText] = useState('');

  const loadProperties = async () => {
    try {
      const res = await hostListProperties(accessToken, { limit: 100 });
      setProperties(res?.items || res || []);
    } catch (_) {
      // ignore silently
    }
  };

  const load = async (pageNum = page) => {
    setLoading(true);
    try {
      const data = await hostReviewsApi.list(accessToken, { ...filters, page: pageNum, limit });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || pageNum);
      const totalReviews = data.total || 0;
      const countInPage = (data.items || []).length || 0;
      const avg =
        totalReviews > 0 && countInPage > 0
          ? (data.items || []).reduce((s, r) => s + Number(r.rating_overall || 0), 0) / countInPage
          : 0;
      const responded = (data.items || []).filter((r) => r.host_response).length;
      setStats({
        avg: avg.toFixed(1),
        total: totalReviews,
        responseRate: totalReviews ? Math.round((responded / totalReviews) * 100) : 0,
      });
    } catch (err) {
      toast.error(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.property_id, filters.status, filters.rating]);

  const openDetail = async (id) => {
    try {
      const res = await hostReviewsApi.get(accessToken, id);
      setActive(res.review);
      setEvents(res.events || []);
      setResponseText(res.review?.host_response || '');
    } catch (err) {
      toast.error(err.message || 'Failed to load review');
    }
  };

  const respond = async () => {
    if (!active) return;
    try {
      await hostReviewsApi.respond(accessToken, active.id, responseText);
      toast.success('Response posted');
      setActive({ ...active, host_response: responseText });
      load(page);
    } catch (err) {
      toast.error(err.message || 'Failed to respond');
    }
  };

  const flag = async () => {
    if (!active) return;
    try {
      await hostReviewsApi.report(accessToken, active.id, 'Flagged by host');
      toast.success('Flagged to admin');
    } catch (err) {
      toast.error(err.message || 'Failed to flag');
    }
  };

  return (
    <HostSection title="Reviews" description="Monitor guest feedback and respond to keep your ratings healthy.">
      <div className="p-6 space-y-5">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <SummaryCard label="Average rating" value={stats.avg || '0.0'} />
          <SummaryCard label="Total reviews" value={stats.total || 0} />
          <SummaryCard label="Response rate" value={`${stats.responseRate || 0}%`} />
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          <select
            value={filters.property_id}
            onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          <select
            value={filters.rating}
            onChange={(e) => setFilters((f) => ({ ...f, rating: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All ratings</option>
            {[5, 4, 3, 2, 1].map((r) => (
              <option key={r} value={r}>
                {r} stars
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="">All statuses</option>
            <option value="published">Published</option>
            <option value="pending">Pending</option>
            <option value="hidden">Hidden</option>
            <option value="removed">Removed</option>
          </select>
          <input
            type="search"
            placeholder="Search booking/property"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
          <div />
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Property</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Comment</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    Reviews appear after guests complete stays.
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-emerald-50/40">
                  <td className="px-4 py-3 font-semibold text-slate-900">{r.property_title || 'Property'}</td>
                  <td className="px-4 py-3 text-slate-800">{r.rating_overall} / 5</td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.comment?.slice(0, 80)}
                    {r.comment?.length > 80 ? '.' : ''}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`rounded-full px-2 py-1 font-semibold ring-1 ring-inset ${statusTone(r.status)}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openDetail(r.id)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between text-sm text-slate-600">
          <div>
            Page {page} of {Math.max(1, Math.ceil((total || 1) / limit))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => load(Math.max(1, page - 1))}
              className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => load(page + 1)}
              className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
              disabled={page * limit >= total}
            >
              Next
            </button>
          </div>
        </div>

        {active && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-md">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700">{active.property_title}</p>
                <h3 className="text-lg font-semibold text-slate-900">{active.rating_overall} / 5</h3>
                <p className="text-sm text-slate-700">{active.comment}</p>
              </div>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
            <div className="mt-3 text-sm text-slate-600">
              Booking: {active.booking_code} | {active.check_in_date} - {active.check_out_date}
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">Host response</p>
                {!active.host_response ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      rows={3}
                    />
                    <button
                      type="button"
                      onClick={respond}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      Post response
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                    {active.host_response}
                    <div className="mt-2 text-xs text-slate-500">
                      Responded at {new Date(active.host_responded_at || active.updated_at).toLocaleString()}
                    </div>
                  </div>
                )}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={flag}
                    className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    Flag to admin
                  </button>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Timeline</p>
                <div className="mt-2 space-y-2">
                  {events.map((evt) => (
                    <div key={evt.id} className="rounded border border-slate-100 bg-slate-50 px-3 py-2">
                      <p className="font-semibold text-slate-900">{evt.event_type}</p>
                      <p className="text-xs text-slate-500">{new Date(evt.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                  {!events.length && <p className="text-xs text-slate-500">No events yet.</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </HostSection>
  );
};

export default HostReviews;
